import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import PropTypes from 'prop-types';

const SortIcon = ({ column, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== column) return <ArrowUpDown size={16} className="text-gray-400" />;
  return sortConfig.direction === 'asc' ? (
    <ArrowUp size={16} className="text-blue-600" />
  ) : (
    <ArrowDown size={16} className="text-blue-600" />
  );
};

const DataTable = ({ 
  columns, 
  data, 
  sortConfig, 
  onSort, 
  renderExpandedRow,
  expandedRowId 
}) => {
  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 pb-12 relative h-[calc(100vh-280px)]">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.sortable ? 'cursor-pointer' : ''
                } ${column.className || ''}`}
                onClick={() => column.sortable && onSort(column.key)}
              >
                <div className="flex items-center gap-1">
                  <span>{column.header}</span>
                  {column.sortable && (
                    <SortIcon column={column.key} sortConfig={sortConfig} />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              <tr className="hover:bg-gray-50 transition-colors">
                {columns.map((column) => (
                  <td
                    key={`${column.key}-${rowIndex}`}
                    className={`px-4 py-3 text-sm ${
                      column.cellClassName || 'text-gray-900'
                    }`}
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
              {expandedRowId === row.id && renderExpandedRow && (
                <tr className="bg-gray-50">
                  <td colSpan={columns.length}>
                    {renderExpandedRow(row)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      className: PropTypes.string,
      cellClassName: PropTypes.string,
      render: PropTypes.func
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc'])
  }),
  onSort: PropTypes.func,
  renderExpandedRow: PropTypes.func,
  expandedRowId: PropTypes.any
};

export default DataTable;